import { FC, useCallback, useState } from 'react';
import { Box, ButtonProps } from '@wix/design-system';
import StatusToastNotification from '@components/StatusToastNotification';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { viewTemplatePath } from '@components/Hooks/useRouter';
import { useTemplateContext } from '@components/Contexts/TemplateContext';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import { useTemplateFavorites } from '@components/Templates/Favorites/useTemplateFavorites';
import { useAutoFavoriteFromQueryParam } from '@components/Templates/Favorites/useAutoFavoriteFromQueryParam';
import { redirectToLoginWithFavorite } from '@utils/urlUtils';
import { ENABLE_TEMPLATE_FAVORITES_SPEC } from '@utils/constants';
import FavoriteButton from '@components/Templates/Favorites/FavoriteButton';
import { FAVORITES_BI_CLICK_TYPES } from '@components/Templates/Favorites/useFavoritesBi';
import PurchaseTemplateButton from '../PurchaseTemplateButton';
import ViewTemplateButton from './ViewTemplateButton';
import { useTemplateEntry } from '@components/Hooks/useTemplateEntry';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';

interface PurchaseCTAProps {
  buttonSize?: ButtonProps['size'];
  className?: string;
  isMobileView: boolean;
  setPurchaseError: (isError: boolean) => void;
}

const PurchaseCTAs: FC<PurchaseCTAProps> = ({
  buttonSize,
  className,
  isMobileView,
  setPurchaseError,
}) => {
  const { template, language } = useTemplateContext();
  const { userDetails, loading: isHeaderLoading } = useHeaderContext();
  const { logBiClick } = useTemplateBi({ is_mobile: isMobileView });
  const { entry } = useTemplateEntry();
  const { experiments } = useExperiments({ readOnly: true });
  const { t } = useTranslation();

  const showFavoriteButton = experiments.enabled(
    ENABLE_TEMPLATE_FAVORITES_SPEC,
  );
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [failedToggleAction, setFailedToggleAction] = useState<
    'add' | 'remove' | null
  >(null);
  const errorKeySuffix = isMobileView ? '.mobile' : '';
  const {
    isTemplateFavorite,
    toggleFavoriteTemplate,
    isTogglePending,
    pendingFavoriteListingId,
  } = useTemplateFavorites({
    enabled: showFavoriteButton && Boolean(userDetails?.email),
    onToggleError: (shouldFavorite) =>
      setFailedToggleAction(shouldFavorite ? 'add' : 'remove'),
  });

  useAutoFavoriteFromQueryParam({
    enabled: showFavoriteButton && Boolean(userDetails?.email),
    toggle: (id, opts) =>
      toggleFavoriteTemplate({ listingId: id, shouldFavorite: true }, opts),
    templateIds: template.id ? [template.id] : [],
    onSuccess: () => setShowSuccessToast(true),
  });

  const handleFavoriteToggle = useCallback(
    (listingId: string, shouldFavorite: boolean) => {
      if (!userDetails?.email) {
        redirectToLoginWithFavorite(listingId, language);
        return;
      }
      toggleFavoriteTemplate({ listingId, shouldFavorite });
      logBiClick({
        clickType: shouldFavorite
          ? FAVORITES_BI_CLICK_TYPES.addToFavorites
          : FAVORITES_BI_CLICK_TYPES.removeFromFavorites,
      });
    },
    [language, logBiClick, toggleFavoriteTemplate, userDetails?.email],
  );

  const toast = (
    <>
      {showSuccessToast && (
        <StatusToastNotification
          key={String(showSuccessToast)}
          status="success"
          message={t(
            'templatesFavorites.gallery.statusToast.success.description',
          )}
          onDismiss={() => setShowSuccessToast(false)}
          dismissible
          isMobileView={isMobileView}
        />
      )}
      {failedToggleAction !== null && (
        <StatusToastNotification
          key={failedToggleAction}
          status="error"
          message={t(
            `templatesFavorites.statusToast.${failedToggleAction}Failure${errorKeySuffix}`,
          )}
          onDismiss={() => setFailedToggleAction(null)}
          dismissible
          isMobileView={isMobileView}
        />
      )}
    </>
  );

  const favoriteButton = showFavoriteButton && (
    <FavoriteButton
      listingId={template.id}
      isFavorite={isTemplateFavorite(template)}
      isLoading={
        isHeaderLoading ||
        (isTogglePending && pendingFavoriteListingId === template.id)
      }
      addLabel={t('templatesFavorites.gallery.tooltip.add')}
      removeLabel={t('templatesFavorites.gallery.tooltip.remove')}
      onToggle={handleFavoriteToggle}
      skin="dark"
      size={isMobileView ? 'medium' : 'large'}
    />
  );

  if (isMobileView) {
    return (
      <>
        {toast}
        <Box
          direction="vertical"
          gap="12px"
          className={className}
          dataHook="purchase-buttons"
        >
          <PurchaseTemplateButton
            templateId={template.id!}
            price={template.price}
            buttonSize={buttonSize}
            logBiClick={() => logBiClick({ clickType: 'purchase' })}
            userDetails={userDetails}
            language={language}
            setPurchaseError={setPurchaseError}
            consentModalProps={{
              hasCustomCode: template.hasVeloOrBlocks,
              onModalBiClick: (clickType: string) => logBiClick({ clickType }),
              isMobileView,
            }}
            privacyPolicyUrl={template?.privacyPolicyUrl ?? undefined}
            fullWidth
          />
          {showFavoriteButton ? (
            <Box gap="12px" verticalAlign="middle">
              <Box flexGrow={1}>
                <ViewTemplateButton
                  buttonSize={buttonSize}
                  viewTemplatePath={viewTemplatePath}
                  template={template}
                  entry={entry}
                  logBiClick={({ clickType }) => logBiClick({ clickType })}
                  fullWidth
                />
              </Box>
              {favoriteButton}
            </Box>
          ) : (
            <ViewTemplateButton
              buttonSize={buttonSize}
              viewTemplatePath={viewTemplatePath}
              template={template}
              entry={entry}
              logBiClick={({ clickType }) => logBiClick({ clickType })}
              fullWidth
            />
          )}
        </Box>
      </>
    );
  }

  return (
    <>
      {toast}
      <Box
        gap="12px"
        display="flex"
        className={className}
        dataHook="purchase-buttons"
        verticalAlign="middle"
      >
        {favoriteButton}
        <ViewTemplateButton
          buttonSize={buttonSize}
          viewTemplatePath={viewTemplatePath}
          template={template}
          entry={entry}
          logBiClick={({ clickType }) => logBiClick({ clickType })}
        />
        <PurchaseTemplateButton
          templateId={template.id!}
          price={template.price}
          buttonSize={buttonSize}
          logBiClick={() => logBiClick({ clickType: 'purchase' })}
          userDetails={userDetails}
          language={language}
          setPurchaseError={setPurchaseError}
          consentModalProps={{
            hasCustomCode: template.hasVeloOrBlocks,
            onModalBiClick: (clickType: string) => logBiClick({ clickType }),
            isMobileView,
          }}
          privacyPolicyUrl={template?.privacyPolicyUrl ?? undefined}
        />
      </Box>
    </>
  );
};

export default PurchaseCTAs;
